import axios, { AxiosRequestConfig } from 'axios';
import store from '../store/store';
import router from '../route/router';
import { QualityGoalType } from '@/interfaces/types/QualityGoal';
import { LabelsetType } from '@/interfaces/types/Labelset';
import {
  camelToSnakeQualityGoalType,
  snakeToCamelLabelsetType,
  snakeToCamelQualityGoalType,
  __Labelset_Type__,
  __Quality_Goal_Type__,
} from './typeConverter';

export const axiosApiInstance = axios.create();

axiosApiInstance.interceptors.request.use(
  async (config: AxiosRequestConfig) => {
    let storedAccessToken = store.getters.accessToken;

    if (storedAccessToken === '') {
      try {
        const response = await axiosApiRefreshToken.post(
          `/userapi/v1/reissue`,
          {
            user_id: store.getters.userId,
          },
        );
        console.log('response:', response);
        if (response.status === 201) {
          console.log(response);
          const newAccessToken = response.data.accessToken;
          const newRefreshToken = response.data.refreshToken;

          store.commit('setToken', {
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
          });
          storedAccessToken = newAccessToken;
        } else {
          //로그아웃 후 접근하지 못하는 resource로 접근하려 할 때
          throw new Error('need to login again');
        }
      } catch (err) {
        if (router.currentRoute.fullPath !== '/login') {
          console.log('session expired');
          router.push('/login');
        }
      }
    }

    config.headers.Authorization = `Bearer ${storedAccessToken}`;
    config.headers.Accept = `application/json`;

    return config;
  },
  async (error) => {
    return Promise.reject(error);
  },
);

axiosApiInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      console.log('auth error: get reissued');
      try {
        const response = await axiosApiRefreshToken.post(
          `/userapi/v1/reissue`,
          {
            user_id: store.getters.userId,
          },
        );

        if (response.status === 201) {
          const newAccessToken = response.data.result.accessToken;
          const newRefreshToken = response.data.result.refreshToken;

          store.commit('setToken', {
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
          });
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          return axios(originalRequest);
        } else {
          throw new Error('세션 만료');
        }
      } catch (reissueError) {
        if (router.currentRoute.fullPath !== '/login') {
          console.log('session expired');
          router.push('/login');
        }
      }
    }
    return Promise.reject(error);
  },
);

export const axiosApiRefreshToken = axios.create();

axiosApiRefreshToken.interceptors.request.use(
  async (config: AxiosRequestConfig) => {
    const storedRefreshToken = store.getters.refreshToken;

    if (storedRefreshToken === '') {
      throw new Error('RefreshToken is empty');
    } else {
      config.headers.Authorization = `Bearer ${storedRefreshToken}`;
      config.headers.Accept = `application/json`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// #region axios util functions
export async function getAllLabelsetByWorkspaceId(
  workspaceId: string,
): Promise<Partial<LabelsetType>[]> {
  return (
    await axiosApiInstance.get<{
      result: {
        labelset_list: __Labelset_Type__[];
      };
    }>(`/api/v1/workspaces/${workspaceId}/labelsets`)
  ).data.result.labelset_list.map((l) => {
    return {
      labelsetId: l.labelset_id,
      name: l.name,
      description: l.description,
    };
  });
}

export async function getAllQualityGoals(
  workspaceId: string,
): Promise<QualityGoalType[]> {
  return (
    await axiosApiInstance.get<{ result: __Quality_Goal_Type__[] }>(
      `/api/v1/workspaces/${workspaceId}/qualitygoals`,
    )
  ).data.result.map(snakeToCamelQualityGoalType);
}

export async function createQualityGoal(
  workspaceId: string,
  qualityGoal: QualityGoalType,
): Promise<number> {
  try {
    return (
      await axiosApiInstance.post(
        `/api/v1/workspaces/${workspaceId}/qualitygoals`,
        camelToSnakeQualityGoalType(qualityGoal),
      )
    ).status;
  } catch (error) {
    console.error(error);

    if (axios.isAxiosError(error) && error.response) {
      return error.response.status;
    }

    return 500;
  }
}

export async function updateQualityGoal(
  qualityGoalId: string,
  qualityGoal: QualityGoalType,
): Promise<number> {
  try {
    return (
      await axiosApiInstance.patch(
        `/api/v1/qualitygoals/${qualityGoalId}`,
        camelToSnakeQualityGoalType({
          name: qualityGoal.name,
          description: qualityGoal.description,
          qualityGoal: qualityGoal.qualityGoal,
        }),
      )
    ).status;
  } catch (error) {
    console.error(error);

    if (axios.isAxiosError(error) && error.response) {
      return error.response.status;
    }

    return 500;
  }
}

export async function deleteQualityGoal(
  qualityGoalId: string,
): Promise<number> {
  try {
    return (
      await axiosApiInstance.delete(`/api/v1/qualitygoals/${qualityGoalId}`)
    ).status;
  } catch (error) {
    console.error(error);

    if (axios.isAxiosError(error) && error.response) {
      return error.response.status;
    }

    return 500;
  }
}

export async function setLastSelectedQualityGoal(
  labelsetId: string,
  qualityGoalId: string,
): Promise<number> {
  try {
    return (
      await axiosApiInstance.post(
        `/api/v1/labelsets/${labelsetId}/qualitygoal`,
        {
          qualityGoalId: qualityGoalId,
        },
      )
    ).status;
  } catch (error) {
    console.error(error);

    if (axios.isAxiosError(error) && error.response) {
      return error.response.status;
    }

    return 500;
  }
}

export async function removeLastSelectedQualityGoal(
  labelsetId: string,
): Promise<number> {
  try {
    return (
      await axiosApiInstance.delete(
        `/api/v1/labelsets/${labelsetId}/qualitygoal`,
      )
    ).status;
  } catch (error) {
    console.error(error);

    if (axios.isAxiosError(error) && error.response) {
      return error.response.status;
    }

    return 500;
  }
}

export async function getLabelsetById(
  labelsetId: string,
): Promise<LabelsetType> {
  return snakeToCamelLabelsetType(
    (
      await axiosApiInstance.get<{ result: __Labelset_Type__ }>(
        `/api/v1/labelsets/${labelsetId}`,
      )
    ).data.result,
  );
}

export async function setReportTimeline(
  labelsetId: string,
  qualityGoalId: string,
  previousNode: string,
  nextNode: string,
): Promise<LabelsetType> {
  return snakeToCamelLabelsetType(
    (
      await axiosApiInstance.patch<{ result: __Labelset_Type__ }>(
        `/api/v1/labelsets/${labelsetId}/reportTimeline`,
        {
          qualityGoalId: qualityGoalId,
          previousNode: previousNode,
          nextNode: nextNode,
        },
      )
    ).data.result,
  );
}

export async function getLabelDataPaths(
  labelsetId: string,
  labelIdList: string[],
): Promise<Record<string, string>> {
  const resultDict: Record<string, string> = {};
  const response = await axiosApiInstance.post(
    `/api/v1/labelsets/${labelsetId}/labels/search`,
    { labelIdList: labelIdList },
  );
  response.data.result.forEach(
    (l: { label_id: string; data_path: string }) =>
      (resultDict[l.label_id] = l.data_path),
  );
  return resultDict;
}
// #endregion
